/**
 * CompositeTable — renders a table the agent assembled, where cells can say
 * where they came from.
 *
 * WRITTEN ONCE, USED TWICE. This renders in agent chat and, via the UMD bundle,
 * inside a published static webapp. That is why it holds **local state only** —
 * no React context, no data fetching, no router, no localStorage. Everything it
 * needs arrives as props, and the click handler is the caller's. Reaching for a
 * context here is what would break the published-page build.
 *
 * It is deliberately NOT PlatinumTable. A composite has no vec hierarchy, no
 * bands, no significance letters and no virtualisation need, so that
 * component's machinery is all cost; and the cell-click semantics differ —
 * here a click asks "where did this come from?", there it opens structural
 * info about a crosstab cell.
 */
import React, { useMemo } from 'react'
import { Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, useTheme } from '@mui/material'
import type { CellProvenance, ProvenanceSidecar, Unit } from './provenance.generated'

export interface PlatinumLikeData {
  name?: string
  top?: { vecs?: Array<{ label?: unknown }> }
  side?: { vecs?: Array<{ label?: unknown }> }
  cells?: { rows?: Array<{ cell?: Array<{ freq?: unknown }> }> }
  properties?: { decimals?: { frequencies?: unknown } }
}

/**
 * How a cell is SHOWN. The provenance units, plus two that exist only for
 * display, set by the page and never stored on a cell: `int` (a whole number,
 * for counts and 0/1 flags) and `p` (a p value: "< 0.001" below the smallest
 * value its decimals can show).
 */
export type DisplayUnit = Unit | 'int' | 'p'

/** A column's display override; either part may be absent. */
export interface ColumnFormat {
  unit?: DisplayUnit
  decimals?: number
}

export interface CompositeTableProps {
  data: PlatinumLikeData
  /** Cells keyed "row,col" in DATA indices. Absent means no cell is clickable. */
  provenance?: ProvenanceSidecar
  title?: string
  /** Called with DATA indices. Cells without provenance are never clickable. */
  onCellClick?: (row: number, col: number, provenance: CellProvenance) => void
  /** Decimals for every column, in place of the composite's own. Display only. */
  decimals?: number
  /** Per column (top) index: a display unit and/or decimals. null or missing changes nothing. Display only. */
  columnFormats?: Array<ColumnFormat | null | undefined>
}

const DISPLAY_UNITS: readonly DisplayUnit[] = ['fraction', 'percent', 'count', 'raw', 'pp', 'int', 'p']
const P_DEFAULT_DECIMALS = 3
const MAX_DECIMALS = 6

const isDecimals = (d: unknown): d is number => typeof d === 'number' && Number.isInteger(d) && d >= 0 && d <= MAX_DECIMALS

/**
 * Checks a page's format options (an agent writes them, so they are not
 * trusted): a bad unit, or decimals that are not a whole number 0–6, is
 * dropped for that column with a console.warn, and nothing throws.
 */
export function cleanFormatOptions(decimals: unknown, columnFormats: unknown): { decimals?: number; columnFormats?: Array<ColumnFormat | null> } {
  const out: { decimals?: number; columnFormats?: Array<ColumnFormat | null> } = {}
  if (decimals !== undefined) {
    if (isDecimals(decimals)) out.decimals = decimals
    else console.warn(`renderComposite: decimals ${JSON.stringify(decimals)} ignored (a whole number 0–${MAX_DECIMALS})`)
  }
  if (columnFormats === undefined) return out
  if (!Array.isArray(columnFormats)) {
    console.warn('renderComposite: columnFormats ignored (an array, one entry per column)')
    return out
  }
  out.columnFormats = Array.from(columnFormats, (entry: unknown, col) => {
    if (entry === null || entry === undefined) return null
    if (typeof entry !== 'object' || Array.isArray(entry)) {
      console.warn(`renderComposite: columnFormats[${col}] ignored (an object or null)`)
      return null
    }
    const e = entry as Record<string, unknown>
    const format: ColumnFormat = {}
    if (e.unit !== undefined) {
      if (DISPLAY_UNITS.includes(e.unit as DisplayUnit)) format.unit = e.unit as DisplayUnit
      else console.warn(`renderComposite: columnFormats[${col}].unit ${JSON.stringify(e.unit)} ignored (one of ${DISPLAY_UNITS.join(', ')})`)
    }
    if (e.decimals !== undefined) {
      if (isDecimals(e.decimals)) format.decimals = e.decimals
      else console.warn(`renderComposite: columnFormats[${col}].decimals ${JSON.stringify(e.decimals)} ignored (a whole number 0–${MAX_DECIMALS})`)
    }
    return format.unit || format.decimals !== undefined ? format : null
  })
  return out
}

/**
 * Present a stored composite value.
 *
 * Values come from `freq` and are stored in whatever `unit` the cell declares —
 * compose_table writes them there precisely because nothing scales `freq`. So
 * the only conversion here is fraction -> percentage; a 'percent' value is
 * already a percentage and must not be multiplied again.
 *
 * THE TOOL NEVER ROUNDS — this is the one place a composite value is rounded
 * for display (design/2026-09-10-composite-table-toolkit.md §3.1). `pp`
 * (percentage points, a derived difference of two percentages) is signed and
 * rounded the same one decimal place as `percent`, so "+0.6pp" reads as a
 * point-and-a-bit swing rather than "0.6%" — a tenth of what actually
 * happened. `raw` rounds to `decimals` (the composite's own
 * properties.decimals.frequencies, which compose_table sets from the spec's
 * optional `decimals` or from what the values actually need) when given;
 * with none supplied it shows the value exactly, as before.
 *
 * `fixed` is decimals the page chose (renderComposite's `decimals` or a
 * column's). It sets the places for every unit but `int`; without it each
 * unit keeps the places above. `int` rounds to a whole number with thousands
 * separators. `p` shows `fixed` places (3 by default), and "< 0.001" (for 3)
 * for anything smaller than the last place can show, a stored 0 included.
 */
export function formatCellValue(value: number, unit: DisplayUnit | undefined, decimals?: number, fixed?: number): string {
  if (!Number.isFinite(value)) return '—'
  const places = typeof fixed === 'number' ? fixed : 1
  switch (unit) {
    case 'fraction':
      return `${(value * 100).toFixed(places)}%`
    case 'percent':
      return `${value.toFixed(places)}%`
    case 'pp':
      return `${value >= 0 ? '+' : ''}${value.toFixed(places)}pp`
    case 'count':
      return typeof fixed === 'number'
        ? value.toLocaleString('en-GB', { minimumFractionDigits: fixed, maximumFractionDigits: fixed })
        : value.toLocaleString()
    case 'int':
      return Math.round(value).toLocaleString('en-GB')
    case 'p': {
      const d = typeof fixed === 'number' ? fixed : P_DEFAULT_DECIMALS
      const floor = 10 ** -d
      return value < floor ? `< ${floor.toFixed(d)}` : value.toFixed(d)
    }
    case 'raw': {
      const d = typeof fixed === 'number' ? fixed : decimals
      return typeof d === 'number' ? value.toFixed(d) : String(value)
    }
    default:
      // No unit means no provenance for this cell. Show the number plainly
      // rather than guessing a scale — guessing is the 100x failure.
      return String(value)
  }
}

const labelsOf = (edge: { vecs?: Array<{ label?: unknown }> } | undefined, fallback: string): string[] =>
  (edge?.vecs ?? []).map((v, i) => (typeof v?.label === 'string' && v.label.length > 0 ? v.label : `${fallback} ${i + 1}`))

export const CompositeTable: React.FC<CompositeTableProps> = ({ data, provenance, title, onCellClick, decimals: allDecimals, columnFormats }) => {
  const theme = useTheme()

  // Labels always come from the data. Hard-coding them is how a table ends up
  // showing "undefined" or, worse, a stale label beside a fresh number.
  const columnLabels = useMemo(() => labelsOf(data?.top, 'Column'), [data])
  const rawDecimals = data?.properties?.decimals?.frequencies
  const decimals = typeof rawDecimals === 'number' ? rawDecimals : undefined
  const rowLabels = useMemo(() => labelsOf(data?.side, 'Row'), [data])
  const rows = useMemo(() => data?.cells?.rows ?? [], [data])

  const provFor = (row: number, col: number): CellProvenance | undefined => provenance?.cells?.[`${row},${col}`]
  // A column's format wins over the table's; both change the text only, never
  // the value, the provenance or what a click reports.
  const show = (value: number, col: number, unit: Unit | undefined): string => {
    const format = columnFormats?.[col]
    // "Show as a percentage" on a cell stored as a fraction still multiplies by 100.
    const shown = format?.unit === 'percent' && unit === 'fraction' ? 'fraction' : (format?.unit ?? unit)
    return formatCellValue(value, shown, decimals, format?.decimals ?? allDecimals)
  }

  return (
    <Box>
      {(title || data?.name) && (
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
          {title || data?.name}
        </Typography>
      )}
      <TableContainer sx={{ overflowX: 'auto' }}>
        <Table size="small" aria-label={title || data?.name || 'Composite table'}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }} />
              {columnLabels.map((label, j) => (
                <TableCell key={j} align="right" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                  {label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rowLabels.map((rowLabel, i) => (
              <TableRow key={i} hover>
                <TableCell component="th" scope="row" sx={{ fontWeight: 500, whiteSpace: 'nowrap' }}>
                  {rowLabel}
                </TableCell>
                {columnLabels.map((_, j) => {
                  const raw = rows[i]?.cell?.[j]?.freq
                  const value = typeof raw === 'number' ? raw : NaN
                  const prov = provFor(i, j)
                  const clickable = Boolean(prov && onCellClick)
                  return (
                    <TableCell
                      key={j}
                      align="right"
                      // A cell with no provenance gets NO click handler and no
                      // affordance. Offering one and then showing nothing is
                      // worse than plain text.
                      onClick={clickable ? () => onCellClick!(i, j, prov!) : undefined}
                      role={clickable ? 'button' : undefined}
                      tabIndex={clickable ? 0 : undefined}
                      onKeyDown={
                        clickable
                          ? (e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault()
                                onCellClick!(i, j, prov!)
                              }
                            }
                          : undefined
                      }
                      aria-label={
                        clickable
                          ? `${rowLabel}, ${columnLabels[j]}: ${show(value, j, prov?.unit)} — show where this came from`
                          : undefined
                      }
                      sx={{
                        whiteSpace: 'nowrap',
                        fontVariantNumeric: 'tabular-nums',
                        ...(clickable
                          ? {
                              cursor: 'pointer',
                              textDecoration: 'underline',
                              textDecorationStyle: 'dotted',
                              textDecorationColor: theme.palette.info.main,
                              textUnderlineOffset: '3px',
                              '&:hover': { color: theme.palette.info.main },
                              '&:focus-visible': {
                                outline: `2px solid ${theme.palette.info.main}`,
                                outlineOffset: '-2px',
                              },
                            }
                          : {}),
                      }}
                    >
                      {show(value, j, prov?.unit)}
                    </TableCell>
                  )
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  )
}

export default CompositeTable
